/**
 * Broadcast panel for one match (EPIC-108, PMA-BCAST-005). Admin + analyst:
 * pick or create a broadcaster, generate the broadcast/control link pair,
 * copy, regenerate, revoke, enable/disable. The control token is shown ONCE
 * (create / regenerate response) and is never retrievable again.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  buildBroadcastUrl,
  buildControlUrl,
  createBroadcaster,
  createBroadcastSession,
  listBroadcasters,
  listBroadcastSessions,
  reenableBroadcastSession,
  regenerateBroadcastSession,
  revokeBroadcastSession,
  setBroadcastSessionEnabled,
  setBroadcastSessionOnAir,
  type Broadcaster,
  type BroadcastSession,
} from "../../lib/broadcastApi";
import { copyTextToClipboard } from "../../lib/clipboard";
import { useAuthStore } from "../../store/authStore";

interface Props {
  matchId: string;
  onClose?: () => void;
}

type Pending =
  | { sessionId: string; action: "revoke" }
  | {
      sessionId: string;
      action: "regenerate";
      which: "broadcast" | "control" | "both";
    }
  | null;

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
};

export default function BroadcastPanel({ matchId, onClose }: Props) {
  const { t } = useTranslation("broadcast");
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === "admin";

  const [broadcasters, setBroadcasters] = useState<Broadcaster[]>([]);
  const [sessions, setSessions] = useState<BroadcastSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState("");
  const [newName, setNewName] = useState("");
  const [newPrimary, setNewPrimary] = useState("#0ea5e9");
  const [newSecondary, setNewSecondary] = useState("#1e293b");
  const [fresh, setFresh] = useState<Record<string, string>>({});
  const [copiedKey, setCopiedKey] = useState("");
  const [pending, setPending] = useState<Pending>(null);
  const [which, setWhich] = useState<"broadcast" | "control" | "both">(
    "control",
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        listBroadcasters(),
        listBroadcastSessions(matchId),
      ]);
      setBroadcasters(b);
      setSessions(s);
      setError("");
    } catch (err) {
      setError(errorText(err, t("panel.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [matchId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = async (key: string, text: string) => {
    if (await copyTextToClipboard(text)) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true);
    try {
      await fn();
      setError("");
    } catch (err) {
      setError(errorText(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    run(async () => {
      let broadcasterId = selected;
      if (!broadcasterId && isAdmin && newName.trim()) {
        const created = await createBroadcaster({
          name: newName.trim(),
          primary_color: newPrimary,
          secondary_color: newSecondary,
        });
        broadcasterId = created.broadcaster_id;
        setNewName("");
      }
      if (!broadcasterId) return;
      const session = await createBroadcastSession(matchId, broadcasterId);
      if (session.control_token) {
        setFresh((f) => ({
          ...f,
          [session.session_id]: session.control_token!,
        }));
      }
      await load();
    }, t("panel.generateFailed"));

  const confirmPending = () => {
    if (!pending) return;
    const p = pending;
    setPending(null);
    return run(async () => {
      if (p.action === "revoke") {
        await revokeBroadcastSession(p.sessionId);
      } else {
        const res = await regenerateBroadcastSession(p.sessionId, p.which);
        if (res.control_token) {
          setFresh((f) => ({ ...f, [res.session_id]: res.control_token! }));
        }
      }
      await load();
    }, t("panel.actionFailed"));
  };

  const toggle = (s: BroadcastSession) =>
    run(async () => {
      await setBroadcastSessionEnabled(s.session_id, !s.enabled);
      await load();
    }, t("panel.actionFailed"));

  const toggleOnAir = (s: BroadcastSession) =>
    run(async () => {
      await setBroadcastSessionOnAir(s.session_id, s.state !== "on_air");
      await load();
    }, t("panel.actionFailed"));

  const reenable = (s: BroadcastSession) =>
    run(async () => {
      await reenableBroadcastSession(s.session_id);
      await load();
    }, t("panel.actionFailed"));

  const btn =
    "rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";

  return (
    <div
      data-testid="broadcast-panel"
      className="space-y-4 text-sm text-gray-800"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {t("panel.title")}
          </h3>
          <p className="text-xs text-gray-600">{t("panel.subtitle")}</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={btn}
            data-testid="broadcast-panel-close"
          >
            {t("panel.close")}
          </button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          data-testid="broadcast-panel-error"
          className="rounded-md border border-red-300 bg-red-50 p-2 text-red-800"
        >
          {error}
        </p>
      )}

      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <label
          className="block text-xs font-medium text-gray-700"
          htmlFor="bp-broadcaster"
        >
          {t("panel.broadcaster")}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <select
            id="bp-broadcaster"
            data-testid="broadcast-panel-broadcaster-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1"
          >
            <option value="">{t("panel.pickBroadcaster")}</option>
            {broadcasters.map((b) => (
              <option key={b.broadcaster_id} value={b.broadcaster_id}>
                {b.name}
              </option>
            ))}
          </select>
          {isAdmin && (
            <>
              <span className="text-xs text-gray-500">
                {t("panel.orCreate")}
              </span>
              <input
                data-testid="broadcast-panel-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("panel.newName")}
                aria-label={t("panel.newName")}
                className="rounded-md border border-gray-300 px-2 py-1"
              />
              <input
                type="color"
                aria-label={t("panel.primaryColor")}
                value={newPrimary}
                onChange={(e) => setNewPrimary(e.target.value)}
                className="h-7 w-9"
              />
              <input
                type="color"
                aria-label={t("panel.secondaryColor")}
                value={newSecondary}
                onChange={(e) => setNewSecondary(e.target.value)}
                className="h-7 w-9"
              />
            </>
          )}
          <button
            type="button"
            data-testid="broadcast-panel-generate"
            disabled={busy || (!selected && !(isAdmin && newName.trim()))}
            onClick={generate}
            className="rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {t("panel.generate")}
          </button>
        </div>
      </div>

      {loading && (
        <p data-testid="broadcast-panel-loading" className="text-gray-500">
          {t("panel.loading")}
        </p>
      )}
      {!loading && sessions.length === 0 && (
        <p data-testid="broadcast-panel-empty" className="text-gray-500">
          {t("panel.empty")}
        </p>
      )}

      <ul className="space-y-3">
        {sessions.map((s) => {
          const revoked = Boolean(s.revoked_at);
          // EPIC-109: the server-derived state wins; a pre-EPIC-109 payload
          // (no `state`) falls back to the old enabled / revoked reading.
          const statusKey = revoked
            ? "revoked"
            : (s.state ?? (s.enabled ? "active" : "disabled"));
          const url = buildBroadcastUrl(
            window.location.origin,
            s.broadcast_token,
          );
          const controlToken = fresh[s.session_id];
          const isPending = pending?.sessionId === s.session_id;
          return (
            <li
              key={s.session_id}
              data-testid={`broadcast-session-row-${s.session_id}`}
              data-status={statusKey}
              className="rounded-lg border border-gray-200 p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{s.broadcaster_name}</span>
                <span
                  data-testid={`broadcast-session-status-${s.session_id}`}
                  data-state={statusKey}
                  className="rounded-full bg-gray-100 px-2 py-0.5 text-xs"
                >
                  {t(`panel.status.${statusKey}`)}
                </span>
              </div>

              <div>
                <span className="text-xs font-medium text-gray-600">
                  {t("panel.broadcastLink")}
                </span>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={url}
                    aria-label={t("panel.broadcastLink")}
                    data-testid={`broadcast-session-link-${s.session_id}`}
                    className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-2 py-1 text-xs"
                  />
                  <button
                    type="button"
                    className={btn}
                    data-testid={`broadcast-session-copy-${s.session_id}`}
                    onClick={() => copy(`b-${s.session_id}`, url)}
                  >
                    {copiedKey === `b-${s.session_id}`
                      ? t("copied")
                      : t("copy")}
                  </button>
                </div>
              </div>

              {controlToken && (
                <div
                  data-testid="broadcast-control-link-once"
                  className="rounded-md border border-amber-300 bg-amber-50 p-2"
                >
                  <p className="text-xs font-semibold text-amber-900">
                    {t("panel.controlLinkOnce")}
                  </p>
                  <div className="mt-1 flex gap-2">
                    <input
                      readOnly
                      value={buildControlUrl(
                        window.location.origin,
                        controlToken,
                      )}
                      aria-label={t("panel.controlLink")}
                      data-testid={`broadcast-control-link-${s.session_id}`}
                      className="flex-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs"
                    />
                    <button
                      type="button"
                      className={btn}
                      data-testid={`broadcast-control-copy-${s.session_id}`}
                      onClick={() =>
                        copy(
                          `c-${s.session_id}`,
                          buildControlUrl(window.location.origin, controlToken),
                        )
                      }
                    >
                      {copiedKey === `c-${s.session_id}`
                        ? t("copied")
                        : t("copy")}
                    </button>
                  </div>
                </div>
              )}

              {s.state && (
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-testid={`broadcast-session-screens-${s.session_id}`}
                    className="text-xs text-gray-600"
                  >
                    {t("panel.screens", { count: s.screens_connected ?? 0 })}
                  </span>
                  {(s.state === "on_air" || s.state === "off") && (
                    <button
                      type="button"
                      className={btn}
                      disabled={busy}
                      data-testid={`broadcast-session-onair-${s.session_id}`}
                      onClick={() => toggleOnAir(s)}
                    >
                      {s.state === "on_air"
                        ? t("panel.turnOff")
                        : t("panel.turnOn")}
                    </button>
                  )}
                  {s.state === "expired" && (
                    <button
                      type="button"
                      className={btn}
                      disabled={busy}
                      data-testid={`broadcast-session-reenable-${s.session_id}`}
                      onClick={() => reenable(s)}
                    >
                      {t("panel.reenable")}
                    </button>
                  )}
                </div>
              )}

              {!revoked && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={btn}
                    disabled={busy}
                    data-testid={`broadcast-session-toggle-${s.session_id}`}
                    onClick={() => toggle(s)}
                  >
                    {s.enabled ? t("panel.disable") : t("panel.enable")}
                  </button>
                  <select
                    aria-label={t("panel.regenerateWhich")}
                    value={which}
                    onChange={(e) => setWhich(e.target.value as typeof which)}
                    className="rounded-md border border-gray-300 px-1.5 py-1 text-xs"
                    data-testid={`broadcast-session-which-${s.session_id}`}
                  >
                    <option value="control">{t("panel.whichControl")}</option>
                    <option value="broadcast">
                      {t("panel.whichBroadcast")}
                    </option>
                    <option value="both">{t("panel.whichBoth")}</option>
                  </select>
                  <button
                    type="button"
                    className={btn}
                    disabled={busy}
                    data-testid={`broadcast-session-regenerate-${s.session_id}`}
                    onClick={() =>
                      setPending({
                        sessionId: s.session_id,
                        action: "regenerate",
                        which,
                      })
                    }
                  >
                    {t("panel.regenerate")}
                  </button>
                  <button
                    type="button"
                    className={`${btn} text-red-700`}
                    disabled={busy}
                    data-testid={`broadcast-session-revoke-${s.session_id}`}
                    onClick={() =>
                      setPending({ sessionId: s.session_id, action: "revoke" })
                    }
                  >
                    {t("panel.revoke")}
                  </button>
                </div>
              )}

              {isPending && (
                <div
                  role="alertdialog"
                  data-testid={`broadcast-session-confirm-${s.session_id}`}
                  className="rounded-md border border-red-200 bg-red-50 p-2 text-xs"
                >
                  <p>
                    {pending?.action === "revoke"
                      ? t("panel.confirmRevoke")
                      : t("panel.confirmRegenerate")}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className={btn}
                      data-testid={`broadcast-session-confirm-yes-${s.session_id}`}
                      onClick={confirmPending}
                    >
                      {t("panel.confirm")}
                    </button>
                    <button
                      type="button"
                      className={btn}
                      onClick={() => setPending(null)}
                    >
                      {t("panel.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
